/**
 * followup-rules-dialog.tsx — "Chủ động theo dõi khách hàng": hộp danh sách quy tắc theo dõi + hộp "Tạo / Sửa quy tắc".
 *
 *   Danh sách: tên, thời gian ("10 phút sau phản hồi cuối của AI"), đối tượng, số lần đã kích hoạt, công tắc bật/tắt, sửa, xoá.
 *   Hộp tạo: tên (mặc định "Quy tắc theo dõi-<yyyymmddHHMMss>"), mốc + số + đơn vị, hướng dẫn cho AI (≤ 5000 ký tự,
 *   chèn biến/công cụ, link "Ví dụ"), khách hàng: tất cả / được chỉ định (trình dựng điều kiện), Huỷ / Xác nhận.
 * Lưu là có hiệu lực NGAY (không phiên bản hoá) — ghi rõ trên hộp.
 */
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { BellRing, Info, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { ConditionBuilder } from './condition-builder'
import { InsertVariableButton } from './insert-variable-button'
import { conditionProblem, insertAtCursor, type CollectAttribute, type Condition } from './bot-settings'
import {
  DELAY_UNITS, FOLLOWUP_EXAMPLES, FOLLOWUP_TRIGGER_OPTIONS, createFollowupRule, deleteFollowupRule, describeDelay, followupKeys,
  joinDelay, splitDelay, updateFollowupRule, useFollowupRules, type DelayUnit, type FollowupRule, type FollowupRuleInput, type FollowupTrigger,
} from './followup-api'

const INSTRUCTION_MAX = 5000

export function FollowupRulesDialog({ open, onOpenChange, botId, canEdit, attributes }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  botId: string
  canEdit: boolean
  attributes: CollectAttribute[]
}) {
  const { t } = useTranslation('aiTrainUi')
  const qc = useQueryClient()
  const { data, isLoading } = useFollowupRules(open ? botId : undefined)
  const rules = data?.rules ?? []
  const [editing, setEditing] = useState<{ rule: FollowupRule | null } | null>(null)
  const refresh = () => qc.invalidateQueries({ queryKey: followupKeys.list(botId) })

  const toggle = async (r: FollowupRule, enabled: boolean) => {
    try { await updateFollowupRule(botId, r.id, { enabled }); await refresh() } catch (err) { toast.error(apiError(err)) }
  }
  const remove = async (r: FollowupRule) => {
    if (!window.confirm(t('followup.confirmDelete', { name: r.name }))) return
    try { await deleteFollowupRule(botId, r.id); await refresh(); toast.success(t('followup.deleted')) } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="h-4 w-4 text-primary" /> {t('followup.title')}</DialogTitle>
          <DialogDescription>
            <Trans t={t} i18nKey="followup.description" components={{ b: <b /> }} />
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{t('followup.ruleCount', { count: rules.length })}</p>
          {canEdit && <Button size="sm" onClick={() => setEditing({ rule: null })} disabled={rules.length >= 20}><Plus className="h-3.5 w-3.5" /> {t('followup.create')}</Button>}
        </div>
        {isLoading ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('followup.loading')}</p>
        ) : rules.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t('followup.empty')}
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {rules.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {describeDelay(r.delayMinutes, r.trigger)} · {r.audience.mode === 'all' ? t('followup.allCustomers') : t('followup.matchConditions', { count: r.audience.conditions.length })}
                    {r.firedCount > 0 ? t('followup.firedCount', { count: r.firedCount, num: r.firedCount.toLocaleString(currentLocale()) }) : ''}
                  </p>
                </div>
                {!r.instruction && <Badge variant="outline" className="text-[10px]">{t('followup.aiDrafts')}</Badge>}
                <Switch checked={r.enabled} onCheckedChange={(v) => void toggle(r, v)} disabled={!canEdit} aria-label={t('followup.enableRule')} />
                {canEdit && (
                  <>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing({ rule: r })} aria-label={t('common:actions.edit')}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => void remove(r)} aria-label={t('common:actions.delete')}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {editing && (
          <FollowupRuleForm
            botId={botId} rule={editing.rule} defaultName={data?.defaultName ?? ''} attributes={attributes} canEdit={canEdit}
            onClose={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await refresh() }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function FollowupRuleForm({ botId, rule, defaultName, attributes, canEdit, onClose, onSaved }: {
  botId: string
  rule: FollowupRule | null
  defaultName: string
  attributes: CollectAttribute[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const { t } = useTranslation('aiTrainUi')
  const init = rule ? splitDelay(rule.delayMinutes) : { amount: 10, unit: 'minutes' as DelayUnit }
  const [name, setName] = useState(rule?.name ?? defaultName)
  const [trigger, setTrigger] = useState<FollowupTrigger>(rule?.trigger ?? 'after_ai_reply')
  const [amount, setAmount] = useState<number>(init.amount)
  const [unit, setUnit] = useState<DelayUnit>(init.unit)
  const [instruction, setInstruction] = useState(rule?.instruction ?? '')
  const [audienceMode, setAudienceMode] = useState<'all' | 'conditions'>(rule?.audience.mode ?? 'all')
  const [conditions, setConditions] = useState<Condition[]>(rule?.audience.mode === 'conditions' ? rule.audience.conditions : [])
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  const insert = (token: string) => {
    const el = ref.current
    const { text, cursor } = insertAtCursor(instruction, token, el?.selectionStart, el?.selectionEnd)
    setInstruction(text)
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(cursor, cursor) })
  }

  const submit = async () => {
    if (!name.trim()) return void toast.error(t('followup.form.nameRequired'))
    const delayMinutes = joinDelay(amount, unit)
    if (!Number.isFinite(delayMinutes) || delayMinutes < 1) return void toast.error(t('followup.form.minDelay'))
    if (delayMinutes > 30 * 1440) return void toast.error(t('followup.form.maxDelay'))
    if (audienceMode === 'conditions') {
      if (!conditions.length) return void toast.error(t('followup.form.needCondition'))
      for (const c of conditions) { const p = conditionProblem(c); if (p) return void toast.error(t('followup.form.conditionProblem', { problem: p })) }
    }
    const input: FollowupRuleInput = {
      name: name.trim(), trigger, delayMinutes, instruction: instruction.trim(), enabled: rule?.enabled ?? true,
      audience: audienceMode === 'all' ? { mode: 'all' } : { mode: 'conditions', conditions },
    }
    setBusy(true)
    try {
      if (rule) await updateFollowupRule(botId, rule.id, input)
      else await createFollowupRule(botId, input)
      toast.success(rule ? t('followup.form.savedRule') : t('followup.form.createdRule'))
      await onSaved()
    } catch (err) { toast.error(apiError(err)) } finally { setBusy(false) }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rule ? t('followup.form.editTitle') : t('followup.form.createTitle')}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> {t('followup.form.onceNote')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">{t('followup.form.name')}</Label>
            <Input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t('followup.form.timing')}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={trigger} onValueChange={(v) => setTrigger(v as FollowupTrigger)} disabled={!canEdit}>
                <SelectTrigger className="h-9 w-64"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min={1} className="h-9 w-24" value={amount} onChange={(e) => setAmount(Math.max(1, Math.round(Number(e.target.value) || 1)))} disabled={!canEdit} aria-label={t('followup.form.amount')} />
              <Select value={unit} onValueChange={(v) => setUnit(v as DelayUnit)} disabled={!canEdit}>
                <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{DELAY_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{t('followup.form.thenFollow')}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs">{t('followup.form.instruction')}</Label>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild><button type="button" className="text-xs text-primary hover:underline">{t('followup.form.examples')}</button></PopoverTrigger>
                  <PopoverContent align="end" className="w-80 space-y-1 p-2">
                    <p className="px-1 text-[11px] text-muted-foreground">{t('followup.form.insertHint')}</p>
                    {FOLLOWUP_EXAMPLES.map((ex) => (
                      <button key={ex.title} type="button" className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted" onClick={() => setInstruction((v) => (v.trim() ? `${v.trim()}\n${ex.text}` : ex.text))}>
                        <span className="font-medium">{ex.title}</span>
                        <span className="block text-xs text-muted-foreground">{ex.text}</span>
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
                <InsertVariableButton botId={botId} onInsert={insert} attributes={attributes} disabled={!canEdit} />
              </div>
            </div>
            <textarea
              ref={ref} maxLength={INSTRUCTION_MAX} value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={!canEdit}
              className="min-h-[120px] w-full resize-y rounded-md border bg-transparent p-3 text-sm outline-none focus:ring-1 focus:ring-ring"
              placeholder={t('followup.form.instructionPlaceholder')}
            />
            <p className="text-right text-[11px] text-muted-foreground">{instruction.length}/{INSTRUCTION_MAX}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t('followup.form.audience')}</Label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={audienceMode === 'all'} onChange={() => setAudienceMode('all')} disabled={!canEdit} /> {t('followup.allCustomers')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={audienceMode === 'conditions'} onChange={() => setAudienceMode('conditions')} disabled={!canEdit} /> {t('followup.form.specified')}
              <span className="text-xs text-muted-foreground" title={t('followup.form.specifiedTitle')}>{t('followup.form.specifiedHint')}</span>
            </label>
            {audienceMode === 'conditions' && (
              <div className="rounded-md bg-muted/30 p-3">
                <ConditionBuilder botId={botId} conditions={conditions} onChange={setConditions} attributes={attributes} disabled={!canEdit}
                  newCondition={{ variable: 'customer.tags', op: 'in', value: [] }} />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canEdit && <Button onClick={() => void submit()} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t('common:actions.confirm')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  )
}
